/**
 * Impressum, Datenschutzerklärung und Nutzungsbedingungen der Anwendung — **als
 * Daten**, die die Web-App (`apps/web/src/routes/legal.tsx`) und die Mobile-App
 * (`apps/mobile/src/screens/legal.tsx`) mit eigenen Bausteinen darstellen.
 *
 * **Warum Daten und nicht JSX.** Bis HM-158 standen die Texte als JSX in der
 * Web-App, und die Mobile-App öffnete sie im Browser — als zweiter Task, der aussah
 * wie eine zweimal geöffnete App. Sie in der App ein zweites Mal zu schreiben wäre
 * die Doppelung, gegen die dieses Paket steht: zwei Fassungen derselben Zusage, von
 * denen die veraltete erst auffällt, wenn sich jemand auf sie beruft. JSX selbst
 * taugt nicht als gemeinsame Form — DOM und React Native kennen verschiedene
 * Elemente.
 *
 * **Der Wortlaut ist der der Web-Seite**, beim Umzug byte-gleich gegen ihr
 * gerendertes HTML geprüft. Eine Stelle ist dabei berichtigt: In § 3 der
 * Datenschutzerklärung fehlte vor „im Arbeitsspeicher" das Leerzeichen — JSX
 * verschluckt einen Zeilenumbruch vor einem Element.
 *
 * **Übernommen ist der Aufbau des Entwurfs, nicht jede Zusage** (HM-061). Die
 * sieben Aussagen, die gegen den Code geprüft und geändert wurden, und warum,
 * stehen im Kopf von `apps/web/src/routes/legal.tsx`. Wer hier einen Satz ändert,
 * prüft ihn gegen das, was das Produkt tut — und ändert bei den Nutzungsbedingungen
 * `TERMS_VERSION` mit.
 *
 * **Was wir nicht wissen, entfällt** (Regel 7): Eine fehlende Kontaktangabe nimmt
 * ihre Zeile mit, und die Fußzeile sagt einmal, was fehlt (`OPEN_ENTRIES`).
 */
import {
  BACKUP_RETENTION_DAYS,
  CODE_LIFETIME_MINUTES,
  CODE_MAX_ATTEMPTS,
  DEVICE_COMMAND_RETENTION_DAYS,
  EXPIRED_RECORD_GRACE_DAYS,
  INVITATION_LIFETIME_DAYS,
  PENDING_REGISTRATION_MAX_AGE_HOURS,
  RAW_RETENTION_DAYS,
  SESSION_HOURS,
  SESSION_REMEMBER_DAYS,
  TERMS_VERSION,
  USAGE_EVENT_RETENTION_DAYS,
} from "./app-facts.js";
import { CONTACT, DATA_PROTECTION_OFFICER, EU_ONLY_CONFIRMED, PROCESSORS, PROCESSORS_COMPLETE, PRODUCT, PROVIDER, SUPERVISORY_AUTHORITY } from "./provider.js";

/* --------------------------------------------------------------------------
 * Die Form
 * ----------------------------------------------------------------------- */

export type LegalDocumentId = "imprint" | "privacy" | "terms";

/** Ein Stück Fließtext — schlicht, hervorgehoben, als Verweis oder in Monoschrift. */
export type LegalInline =
  | string
  | { readonly kind: "strong" | "em" | "code"; readonly text: string }
  /** Ein Verweis nach außen — `tel:`, `mailto:`. */
  | { readonly kind: "link"; readonly href: string; readonly text: string }
  /** Ein Verweis auf eines der drei Dokumente. */
  | { readonly kind: "document"; readonly document: LegalDocumentId; readonly text: string };

export type LegalText = readonly LegalInline[];

/** Ein Block aus Etikett und mehrzeiliger Angabe — die Spalten im Impressum. */
export interface LegalFactBlock {
  readonly label: string;
  readonly lines: readonly LegalText[];
  /**
   * Eine leisere Zeile darunter — „keine Support-Hotline". Sie steht abgesetzt
   * und nicht in Klammern hinter der Nummer: Sie ordnet ein, wofür der Weg gedacht
   * ist, und nimmt der Nummer nicht die Eigenschaft, der Kontaktweg zum Anbieter
   * zu sein (§ 5 Abs. 1 Nr. 2 DDG).
   */
  readonly note?: string;
}

export type LegalBlock =
  | { readonly kind: "paragraph"; readonly text: LegalText }
  | { readonly kind: "list"; readonly items: readonly LegalText[] }
  /** Ein abgesetzter Kasten — „Kurz gefasst" der Nutzungsbedingungen. */
  | { readonly kind: "note"; readonly text: LegalText }
  | { readonly kind: "facts"; readonly items: readonly LegalFactBlock[] }
  /** Die gestreifte Tabelle — Registerangaben, Speicherdauern. */
  | { readonly kind: "table"; readonly head?: readonly [string, string]; readonly rows: readonly (readonly [LegalText, LegalText])[] }
  /**
   * Ein nummerierter Abschnitt. Die Nummer steht **im Titel** und nicht als
   * Zähler: Auf sie wird in Auskünften und Schriftsätzen verwiesen, und ein
   * Zähler, der sich beim Einfügen verschiebt, macht jeden älteren Verweis falsch.
   */
  | { readonly kind: "section"; readonly title: string; readonly blocks: readonly LegalBlock[] };

export interface LegalDocumentContent {
  readonly id: LegalDocumentId;
  readonly title: string;
  /** Die Zeile unter dem Titel — nach welcher Vorschrift der Text steht. */
  readonly lead: string;
  readonly blocks: readonly LegalBlock[];
}

/** Die drei Dokumente in der Reihenfolge, in der sie in jeder Leiste stehen. */
export const LEGAL_DOCUMENT_IDS: readonly LegalDocumentId[] = ["imprint", "privacy", "terms"];

/** Die Beschriftung im Umschalter — kürzer als der Titel. */
export const LEGAL_DOCUMENT_LABELS: Record<LegalDocumentId, string> = {
  imprint: "Impressum",
  privacy: "Datenschutz",
  terms: "Nutzungsbedingungen",
};

const strong = (text: string): LegalInline => ({ kind: "strong", text });
const em = (text: string): LegalInline => ({ kind: "em", text });
const code = (text: string): LegalInline => ({ kind: "code", text });
const link = (href: string, text: string): LegalInline => ({ kind: "link", href, text });
const doc = (document: LegalDocumentId, text: string): LegalInline => ({ kind: "document", document, text });
const p = (...text: LegalInline[]): LegalBlock => ({ kind: "paragraph", text });
const section = (title: string, ...blocks: LegalBlock[]): LegalBlock => ({ kind: "section", title, blocks });
const only = <T>(condition: boolean, ...items: T[]): T[] => (condition ? items : []);

/** Klartext eines Stücks Fließtext — für Suche, Tests und Bildschirmleser. */
export function legalPlainText(text: LegalText): string {
  return text.map((part) => (typeof part === "string" ? part : part.text)).join("");
}

/* --------------------------------------------------------------------------
 * Impressum
 * ----------------------------------------------------------------------- */

/**
 * Die Kontaktzeilen — **nur die, die es gibt** (Regel 7). Beide Wege sind
 * Verweise: § 5 Abs. 1 Nr. 2 DDG verlangt eine *schnelle* Kontaktaufnahme, und
 * eine Nummer, die man auf dem Telefon abtippen muss, ist das weniger als eine,
 * die man antippt. Gewählt wird `phoneHref` — die `(0)` ist im internationalen
 * Format keine zu wählende Ziffer.
 */
function contactLines(): LegalText[] {
  const lines: LegalText[] = [];
  if (CONTACT.phone !== null) {
    lines.push(["Telefon: ", CONTACT.phoneHref === null ? CONTACT.phone : link(`tel:${CONTACT.phoneHref}`, CONTACT.phone)]);
  }
  if (CONTACT.email !== null) lines.push(["E-Mail: ", link(`mailto:${CONTACT.email}`, CONTACT.email)]);
  return lines;
}

function imprint(): readonly LegalBlock[] {
  const contact = contactLines();
  return [
    {
      kind: "facts",
      items: [
        { label: "Anbieter", lines: [[PROVIDER.name], [PROVIDER.street], [PROVIDER.city], [PROVIDER.country]] },
        { label: "Vertreten durch", lines: [[`${PROVIDER.representedBy.role}:`], [PROVIDER.representedBy.person]] },
        ...only<LegalFactBlock>(contact.length > 0, {
          label: "Kontakt",
          lines: contact,
          ...(CONTACT.phoneNote !== null ? { note: CONTACT.phoneNote } : {}),
        }),
      ],
    },
    {
      kind: "table",
      rows: [
        [["Registergericht"], [PROVIDER.court]],
        [["Registernummer"], [code(PROVIDER.registerNumber)]],
        [["Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz"], [code(PROVIDER.vatId)]],
        [["WEEE-Reg.-Nr."], [code(PROVIDER.weeeId)]],
      ],
    },
    section(
      "Verbraucherstreitbeilegung",
      p("Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen."),
    ),
    section(
      "Haftung für Inhalte und Links",
      p(
        "Für eigene Inhalte auf diesen Seiten sind wir nach den allgemeinen Gesetzen verantwortlich. Für Inhalte verlinkter externer Seiten sind deren Betreiber verantwortlich; rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.",
      ),
    ),
    section(
      "Urheberrecht",
      p(
        "Die auf diesen Seiten veröffentlichten Inhalte unterliegen dem deutschen Urheberrecht. Eine Vervielfältigung oder Verwendung außerhalb der gesetzlich zugelassenen Fälle bedarf unserer schriftlichen Zustimmung.",
      ),
    ),
  ];
}

/* --------------------------------------------------------------------------
 * Datenschutzerklärung
 * ----------------------------------------------------------------------- */

function privacy(): readonly LegalBlock[] {
  /**
   * Die Adresse für Datenschutzanliegen: die eigene, sonst die allgemeine. Art. 13
   * Abs. 1 lit. a verlangt die Kontaktdaten des Verantwortlichen, und die allgemeine
   * Adresse erfüllt das.
   */
  const privacyContact = CONTACT.privacyEmail ?? CONTACT.email;

  return [
    /*
     * **Die erste Frage ist nicht „was verarbeitet ihr", sondern „gilt das für
     * mich".** Wer unter der anderen Marke ein Gerät bestellt hat und hier landet,
     * sucht Zahlungsdaten und Lieferadresse. Ein Satz beantwortet das, vor allem
     * anderen. Einen „Kurz gefasst"-Kasten gibt es hier bewusst nicht: Neben dem
     * Geltungsbereich wäre er eine zweite Einleitung und eine zweite Fassung
     * derselben Aussagen.
     */
    p(
      `Diese Erklärung gilt für die Nutzung von ${PRODUCT.name} — der WebApp, den Apps und der Cloud-Anbindung der Geräte. Für den `,
      strong("Kauf"),
      ` eines Geräts gilt sie nicht: Geräte werden unter der Marke ${PRODUCT.shopBrand} verkauft, und dafür gilt die dortige Datenschutzerklärung. Bestell-, Zahlungs- und Lieferdaten verarbeiten wir in ${PRODUCT.name} nicht.`,
    ),

    section(
      "1. Verantwortlicher und Datenschutzbeauftragter",
      p(
        `Verantwortlich für die Verarbeitung ist ${PROVIDER.name}, ${PROVIDER.street}, ${PROVIDER.city}, ${PROVIDER.country}, vertreten durch ${PROVIDER.representedBy.person}.`,
        ...only<LegalInline>(CONTACT.email !== null, " Allgemeine Anfragen richtest du an ", link(`mailto:${CONTACT.email}`, CONTACT.email ?? ""), "."),
      ),
      /*
       * **Der Name allein genügt nicht.** Art. 37 Abs. 7 DSGVO verlangt die
       * Veröffentlichung der Kontaktdaten des Datenschutzbeauftragten. Der Absatz
       * nennt seinen eigenen Weg: Eine Adresse, die zweimal dasteht, lässt offen,
       * welche gemeint ist, wenn jemand tatsächlich etwas zu melden hat.
       */
      ...only<LegalBlock>(
        DATA_PROTECTION_OFFICER !== null,
        p(
          `Unser Datenschutzbeauftragter ist ${DATA_PROTECTION_OFFICER?.name ?? ""}. Du erreichst ihn unter der oben genannten Anschrift`,
          ...only<LegalInline>(
            privacyContact !== null,
            " oder unter ",
            link(`mailto:${privacyContact}`, privacyContact ?? ""),
            ...only<LegalInline>(CONTACT.privacyEmail === null, " — dann bitte mit dem Zusatz „zu Händen des Datenschutzbeauftragten\""),
          ),
          ".",
        ),
      ),
    ),

    section(
      "2. Registrierung und Bestätigung der E-Mail-Adresse",
      p(
        "Für ein Konto verarbeiten wir Vor- und Nachname, E-Mail-Adresse und ein von dir gewähltes Passwort. Das Passwort speichern wir ausschließlich als kryptografischen Hash — im Klartext liegt es zu keinem Zeitpunkt bei uns.",
      ),
      p(
        `Ein Konto entsteht erst, wenn du deine Adresse belegt hast. Bis dahin merken wir deine Angaben in einem gesonderten Datensatz vor und senden einen Sicherheitscode an die angegebene Adresse. Auch dieser Code liegt nur als Hash bei uns. Er gilt ${CODE_LIFETIME_MINUTES} Minuten und verträgt ${CODE_MAX_ATTEMPTS} Eingaben; danach endet der Vorgang. Bleibt die Bestätigung aus, löschen wir den vorgemerkten Datensatz nach ${PENDING_REGISTRATION_MAX_AGE_HOURS} Stunden vollständig — mit ihm verschwinden Name, Adresse und Passwort-Hash.`,
      ),
      p("Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Durchführung vorvertraglicher Maßnahmen und des Vertrags)."),
    ),

    section(
      "3. Anmeldung und Schutz vor Missbrauch",
      p(
        "Bei der Anmeldung prüfen wir E-Mail-Adresse und Passwort. Damit Passwörter nicht durchprobiert werden können, begrenzen wir die Zahl der Versuche je Absender und je angegebener Adresse. Dafür halten wir die IP-Adresse des Aufrufs für wenige Minuten ",
        strong("im Arbeitsspeicher"),
        " unserer Server; sie wird nicht in eine Datenbank geschrieben und nicht ausgewertet. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse ist die Sicherheit der Konten.",
      ),
      p(
        `Zu jeder Anmeldung führen wir eine Sitzung: den Zeitpunkt der Anmeldung, den Zeitpunkt der letzten Nutzung und, sofern erkennbar, die Art des Browsers und des Betriebssystems — etwa „Safari auf iPhone", ohne Versionsnummer. Damit kannst du unter „Profil" sehen, auf welchen Geräten du angemeldet bist, und einzelne Sitzungen beenden. Die vollständige Browserkennung, eine IP-Adresse oder ein Standort werden dabei nicht gespeichert. Eine Sitzung endet ohne weitere Nutzung nach ${SESSION_HOURS} Stunden, mit „Angemeldet bleiben" nach ${SESSION_REMEMBER_DAYS} Tagen; jede Nutzung setzt die Frist neu. Den Eintrag einer beendeten oder abgelaufenen Sitzung löschen wir ${EXPIRED_RECORD_GRACE_DAYS} Tage nach ihrem Ablauf; bis dahin dient er dem Erkennen fremder Zugriffe. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, für das Erkennen fremder Zugriffe Art. 6 Abs. 1 lit. f DSGVO.`,
      ),
      p(
        "Änderungen an deinen Anlagen, Geräten und Zählern halten wir in einem Protokoll fest, damit nachvollziehbar bleibt, wer was geändert hat. Das Protokoll enthält den Vorgang, den Gegenstand und den Zeitpunkt — keine IP-Adressen und keine Gerätekennungen des Browsers. Es gehört zu deinem Konto und wird mit ihm gelöscht.",
      ),
    ),

    section(
      "4. Messwerte, Geräte und Zähler",
      p(
        "Zählerstände und Momentanwerte werden von dem Gerät in deinem Heimnetz erfasst. Ob sie zu uns gelangen, entscheidest du: Ein Gerät im reinen Lokalbetrieb sendet nichts. Verbindest du es mit deinem Konto, übertragen wir Messwerte samt Zeitstempel, den Aufbau deiner Anlage (Anlagen, Zähler, Zählwerke, Kanäle) sowie technische Angaben des Geräts — Seriennummer, Firmware, Verbindungszustand, Feldstärke, Laufzeit.",
      ),
      p(
        "Auf Anforderung meldet ein Gerät zusätzlich eine Diagnose. Sie enthält den Namen deines WLANs und die lokale IP-Adresse des Geräts — die einzigen Angaben in diesem Produkt, die etwas über dein Heimnetz aussagen. Sie stehen darin, weil sie der Grund sind, warum ein Gerät nicht erreichbar ist. Wir überschreiben die Diagnose bei jeder neuen Meldung und führen sie ",
        strong("nicht"),
        " als Zeitreihe: Eine Sammlung davon wäre ein Profil deines Netzes und für die Fehlersuche nicht besser als der letzte Stand.",
      ),
      p(
        "Betreibst du ein HausMeter Gateway, meldet es uns, welche Geräte es in deinem Netz gefunden hat — je Gerät eine Kennung und, soweit auslesbar, Name, Hersteller, Modell, Seriennummer und die Adresse, unter der das Gateway es erreicht. Aus dieser Meldung entsteht ",
        strong("kein"),
        " Gerät und kein Messwert: Sie ist eine Frage an dich. Erst wenn du ein gefundenes Gerät zulässt, übertragen wir dessen Werte; für abgelehnte Geräte speichern wir nichts als die Angabe, dass du sie abgelehnt hast. Die Adresse überschreiben wir bei jeder neuen Meldung und führen sie nicht als Zeitreihe — aus demselben Grund wie bei der Diagnose.",
      ),
      p(
        "Verbrauchsdaten sind aussagekräftig: Aus einem feingliedrigen Lastgang lässt sich ablesen, wann jemand aufsteht, kocht oder verreist. Wir verwenden sie ausschließlich, um dir die Auswertungen in deinem Konto bereitzustellen. Es gibt keine Schnittstelle, über die Verbrauchsdaten an Dritte gingen, und wir werten sie nicht zu anderen Zwecken aus. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO.",
      ),
      p(
        `Gibst du ein Gerät frei, widerrufen wir seine Zugangsdaten; es kann danach nichts mehr an uns senden. Seine Rohmesswerte löschen wir nicht sofort, sondern wie alle Rohmesswerte ${RAW_RETENTION_DAYS} Tage nach ihrer Messung. Die Historie des `,
        em("Zählers"),
        " bleibt bestehen — sie gehört der Anlage und nicht dem Gerät, das sie geliefert hat.",
      ),
    ),

    section(
      "4a. Wohneinheiten und Bewohner",
      p(
        "Eine Anlage mit Mieterstrom führt Wohneinheiten. Der Besitzer der Anlage kann eine Person per E-Mail zu einer Wohneinheit einladen; wir speichern dazu die eingeladene Adresse, die Wohneinheit, das Einzugs- und gegebenenfalls das Auszugsdatum sowie einen Hash des Einladungslinks. Nimmt die Person die Einladung an, entsteht eine",
        strong(" Bewohnung"),
        ": Sie sieht die berechneten Werte ihrer Wohneinheit — Verbrauch, PV-Anteil, Netzanteil — für die Zeit zwischen Einzug und Auszug, und sonst nichts von dieser Anlage.",
      ),
      p(
        "Der Besitzer der Anlage sieht die Werte jeder Wohneinheit seiner Anlage, auch im Viertelstundenraster. Grundlage ist der Vertrag mit dem Besitzer über die Messung seiner Anlage (Art. 6 Abs. 1 lit. b DSGVO); ob und wie ein Vermieter diese Werte gegenüber seinen Mietern verwenden darf, richtet sich nach dem Mietverhältnis und liegt außerhalb dieser Anwendung.",
      ),
      p(
        `Hängt eine Wallbox hinter dem Zähler einer Wohneinheit, kann die dort wohnende Person sie bedienen — Betriebsart und Ladeziel. Jede Änderung speichern wir als Auftrag an das Gerät, mit Zeitpunkt, Einstellung, Ergebnis und dem Konto, das sie ausgelöst hat. Der Besitzer der Anlage sieht an der Wallbox, welche Einstellung wann gesetzt wurde, aber nicht, von wem; die bewohnende Person sieht die Aufträge seit ihrem Einzug. Mit dem Auszug endet die Bedienung. Einen Auftrag löschen wir ${DEVICE_COMMAND_RETENTION_DAYS} Tage nach dem Zeitpunkt, zu dem er gegeben wurde. Grundlage ist die Nutzung der Wallbox durch die bewohnende Person (Art. 6 Abs. 1 lit. b DSGVO).`,
      ),
    ),

    section(
      "4b. Geteilte Anlagen",
      p(
        "Der Besitzer einer Anlage kann sie mit weiteren Personen teilen. Er lädt dazu per E-Mail ein und wählt, ob die eingeladene Person die Anlage nur ",
        strong("lesen"),
        " oder ",
        strong("lesen und ändern"),
        " darf; wir speichern dazu die eingeladene Adresse, die Anlage, die gewählte Berechtigung und einen Hash des Einladungslinks. Nimmt die Person die Einladung an, entsteht eine Teilung: Sie sieht diese Anlage so, wie der Besitzer sie sieht — Geräte, Zähler, Messkonzept und alle Werte, auch die der Wohneinheiten im Viertelstundenraster. Mit der Berechtigung zum Ändern kann sie außerdem Stammdaten, Zähler, Messkonzept und Geräte ändern und eine Wallbox bedienen. Löschen, Archivieren, Teilen und das Einladen von Bewohnern bleiben dem Besitzer vorbehalten.",
      ),
      p(
        "Der Besitzer sieht, mit wem er die Anlage teilt; die Person mit Teilung sieht, wem die Anlage gehört. Beide können die Teilung jederzeit beenden, danach sieht die Person die Anlage nicht mehr. Grundlage ist die Entscheidung des Besitzers, seine Anlage zu teilen, im Rahmen des Vertrags mit ihm (Art. 6 Abs. 1 lit. b DSGVO); sind Werte von Bewohnern betroffen, gilt für ihn dasselbe wie in Abschnitt 4a.",
      ),
    ),

    section(
      "5. Aufruf der WebApp",
      p(
        "Unser Webserver protokolliert erfolgreiche Aufrufe nicht. Festgehalten werden nur Fehler und sicherheitsrelevante Ereignisse; solche Einträge können IP-Adresse, Zeitpunkt und aufgerufene Adresse enthalten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (sicherer Betrieb).",
      ),
      p(
        "Wir setzen ausschließlich technisch notwendige Cookies ein — eines für die Anmeldung, eines gegen seitenübergreifende Anfragefälschung. Es gibt keine Analysedienste Dritter, kein Werbe-Tracking und keine Einbindung fremder Server: Schriften, Symbole und Skripte liefern wir selbst aus, und unsere Inhaltssicherheitsrichtlinie setzt das durch. Welche Ansichten und Funktionen angemeldete Nutzer verwenden, zählen wir selbst, ohne Cookie und ohne fremden Dienst (Abschnitt 5b). Rechtsgrundlage für die Cookies ist § 25 Abs. 2 Nr. 2 TDDDG — eine Einwilligung ist dafür nicht erforderlich, und wir holen deshalb auch keine ein.",
      ),
    ),

    /*
     * **Die App beschreibt sich selbst, weil sie mehr kann als die WebApp**
     * (HM-163): Kamera, Bluetooth und das Heimnetz. Google Play und der App Store
     * verlangen, dass ihre Datenschutzangaben mit dieser Erklärung übereinstimmen —
     * was hier steht, ist gegen `apps/mobile` geprüft: alles Gespeicherte liegt im
     * sicheren Speicher des Systems (`lib/storage.ts`, `expo-secure-store` mit der
     * Vorgabe `WHEN_UNLOCKED`), keine Analyse- oder Werbebibliothek. **Unter iOS
     * überlebt der Schlüsselbund das Löschen der App** und geht in eine
     * verschlüsselte Gerätesicherung ein — deshalb steht der Speicher je System da.
     */
    section(
      "5a. Die App für Android und iOS",
      p(
        "Die App lädst du über Google Play oder den App Store. Dabei verarbeiten Google bzw. Apple Daten über dein Konto bei ihnen und dein Gerät in eigener Verantwortung, nach ihren eigenen Datenschutzerklärungen. Wir erhalten von ihnen nur zusammengefasste Statistiken über Installationen und, soweit du es in den Einstellungen deines Telefons zugelassen hast, Absturzberichte ohne Bezug zu deinem Konto.",
      ),
      p(
        "Die App enthält keine Analysedienste Dritter, keine Werbung und kein Tracking über Apps oder Websites hinweg. Mit unseren Servern spricht sie nur, wenn du mit einem Konto angemeldet bist — dann gilt für Anmeldung, Sitzung und Messwerte dasselbe wie für die WebApp (Abschnitte 2 bis 4), für die Nutzungsstatistik Abschnitt 5b und für Feedback Abschnitt 5c. Im lokalen Betrieb ohne Konto zählt die App nichts.",
      ),
      p(
        strong("Kamera."),
        " Sie dient allein dem Einlesen des Kopplungscodes oder des Etiketts eines Geräts. Das Bild wird auf dem Telefon ausgewertet, weder gespeichert noch übertragen.",
      ),
      p(
        strong("Bluetooth."),
        " Damit richtet die App ein neues Gerät ein: Sie überträgt Name und Passwort deines WLANs, ein Gerätepasswort und auf Wunsch die Kopplung mit deinem Konto unmittelbar an das Gerät — nicht an uns. Deinen Standort ermittelt die App dabei nicht.",
      ),
      p(
        strong("Heimnetz."),
        " Im lokalen Betrieb sucht die App Geräte in deinem Netz und liest sie direkt; diese Werte laufen nicht über unsere Server.",
      ),
      p(
        strong("Auf dem Telefon"),
        " speichert die App im geschützten Speicher des Systems — unter Android im Keystore, unter iOS im Schlüsselbund: den Anmeldeschlüssel deiner Sitzung, die Adressen und Passwörter deiner Geräte im Heimnetz, die WLANs, die du für weitere Einrichtungen gespeichert hast, und deine Einstellungen. Diese Daten gehen nicht an uns. Abmelden löscht den Anmeldeschlüssel.",
      ),
      p(
        "Unter Android sind sie von der Datensicherung ausgenommen, und Deinstallieren löscht alles. Unter iOS gehen sie verschlüsselt in eine Sicherung deines iPhones oder iPads ein, und das System behält Einträge im Schlüsselbund auch nach dem Löschen der App; wer das nicht möchte, meldet sich vor dem Löschen ab und entfernt die Geräte und WLANs in der App.",
      ),
      p("Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO; für den Zugriff auf Kamera, Bluetooth und — unter iOS — das lokale Netzwerk fragt das Telefon dich vorher um Erlaubnis, die du jederzeit in den Einstellungen zurücknehmen kannst."),
    ),

    /*
     * **Die Nutzungsstatistik** (HM-168, ADR 0046). Jede Aussage hier ist gegen
     * den Code geprüft: Gemeldet werden Schlüssel aus dem Katalog in
     * `@hausmeter/domain` (`usage.ts`), die Tageskennung bildet die API
     * (`apps/api/src/usage/usage-salt.ts`), das Salz löscht sie und der Worker
     * nach Tagesende, die Frist ist die TTL von `usage_events`. Die Stunde
     * (HM-174) rechnet der Worker aus dem Zeitpunkt des Ereignisses
     * (`apps/worker/src/usage/rollup.ts`). Wer hier etwas ändert, prüft dort zuerst.
     */
    section(
      "5b. Nutzungsstatistik",
      p(
        `Wenn du angemeldet bist, zählen wir, welche Ansichten der WebApp und der App geöffnet und welche Funktionen benutzt werden — etwa „Zähler · Einstellungen geöffnet" oder „Gerät gekoppelt". Je Ereignis speichern wir dafür Tag und Uhrzeit, die Plattform (WebApp, Android oder iOS), bei der App deren Fassung, die Art und den Namen der Ansicht oder Funktion — ohne die Kennungen von Anlagen, Geräten oder Zählern — und eine Tageskennung.`,
      ),
      p(
        strong("Die Tageskennung"),
        " bilden wir auf unserem Server aus deiner Nutzerkennung und einem Zufallswert, der jeden Tag neu entsteht und nach Ablauf des Tages gelöscht wird. Damit lässt sich zählen, wie viele Personen die Anwendung an einem Tag benutzt haben. Wer es war und ob dieselbe Person an einem anderen Tag wiederkam, lässt sich danach auch von uns nicht mehr feststellen. Eine IP-Adresse, eine Geräte- oder Werbekennung, deinen Namen oder deine E-Mail-Adresse speichern wir dafür nicht, und auf deinem Gerät wird dafür nichts gespeichert oder ausgelesen.",
      ),
      p(
        `Die einzelnen Ereignisse löschen wir nach ${USAGE_EVENT_RETENTION_DAYS} Tagen. Die daraus gebildeten Zahlen — wie oft eine Ansicht an einem Tag geöffnet wurde, von wie vielen Personen, und wie viele Personen in jeder Stunde eines Tages da waren — enthalten keine Kennung mehr; sie bewahren wir ohne Frist auf. Die Auswertung sieht nur der Betreiber der Plattform.`,
      ),
      p(
        `Rechtsgrundlage ist unser berechtigtes Interesse, die Anwendung nach ihrer tatsächlichen Nutzung weiterzuentwickeln (Art. 6 Abs. 1 lit. f DSGVO). Du kannst jederzeit widersprechen: in der WebApp unter „Profil" → „Nutzungsstatistik", in der App unter „Mehr". Danach zählen wir für dein Konto nichts mehr, in WebApp und App.`,
      ),
    ),

    /*
     * **Feedback** (HM-169, ADR 0047). Gegen den Code geprüft: Was mitgeht,
     * bildet die API in `apps/api/src/feedback/feedback.service.ts` aus der
     * Sitzung und der Browserkennung (`feedbackAppLabel`, `feedbackClientLabel`
     * in `@hausmeter/domain`); die Zeile hängt mit `cascade` am Nutzer
     * (`packages/db/src/schema/feedback.ts`). Wer dort etwas ändert, ändert hier.
     */
    section(
      "5c. Feedback",
      p(
        "Unter „Feedback senden\" im Kontomenü der WebApp und unter „Mehr\" in der App kannst du einen Fehler melden oder einen Wunsch äußern. Wir speichern dafür, was du eingibst — Art, Titel und Beschreibung —, dein Konto mit deiner E-Mail-Adresse und den Zeitpunkt. Dazu legt die Anwendung bei, damit sich die Meldung nachvollziehen lässt: die Fassung der App, die Art des Browsers bzw. Geräts und des Betriebssystems ohne Versionsnummer (etwa „Firefox auf Windows\"), die Ansicht, aus der du gesendet hast, und — wenn du sie nicht abwählst — die gewählte Anlage. Was beigelegt wird, siehst du vor dem Senden unter „Wird mitgesendet\". Eine IP-Adresse speichern wir dafür nicht.",
      ),
      p(
        "Die Meldungen sieht nur der Betreiber der Plattform. Nimmt er eine an, übernimmt er Art, Titel und Beschreibung sowie Fassung, Browser bzw. Gerät und Ansicht — ohne deine E-Mail-Adresse und ohne die Anlage — als Aufgabe in unser selbst betriebenes Entwicklungssystem; dort bleibt sie, auch wenn du dein Konto später löschst. Eine Antwort oder Benachrichtigung über den Fortgang gibt es bislang nicht. Schreib in die Beschreibung bitte nichts, was wir zur Bearbeitung nicht brauchen.",
      ),
      p(
        "Wir bewahren eine Meldung auf, bis du dein Konto löschst; dann wird sie mit ihm gelöscht. Rechtsgrundlage ist unser berechtigtes Interesse, Fehler zu beheben und die Anwendung weiterzuentwickeln (Art. 6 Abs. 1 lit. f DSGVO); gesendet wird nur, was du selbst abschickst.",
      ),
    ),

    section(
      "6. Empfänger und Auftragsverarbeiter",
      p(
        "Für Betrieb, Speicherung und E-Mail-Versand arbeiten wir mit Dienstleistern zusammen, die ausschließlich in unserem Auftrag und nach unseren Weisungen tätig sind (Art. 28 DSGVO):",
      ),
      { kind: "list", items: PROCESSORS.map((processor) => [strong(`${processor.purpose}:`), ` ${processor.name}`]) },
      /*
       * **Eine unvollständige Liste sagt, dass sie es ist.** Ohne den Satz läse sich
       * die Aufzählung wie die vollständige — und ein verschwiegener
       * Auftragsverarbeiter ist eine fehlende Pflichtangabe (OQ-036).
       */
      ...only<LegalBlock>(!PROCESSORS_COMPLETE, p("Ein weiterer Dienstleister ist an dieser Stelle noch nicht eingetragen.")),
      p(
        "Unseren Mailserver betreiben wir selbst; er läuft auf einem der oben genannten Server. Ein Versanddienstleister, der deine E-Mails oder deine Adresse in eigener Verantwortung verarbeitete, ist damit nicht beteiligt.",
      ),
      /*
       * **Was nicht bestätigt ist, wird nicht zugesagt** (Regel 9, OQ-039). „Keine
       * Übermittlung in Drittländer" ist eine Zusage über den Standort der Maschinen,
       * nicht über den Sitz der Anbieter (Art. 13 Abs. 1 lit. f DSGVO).
       */
      EU_ONLY_CONFIRMED
        ? p(
            "Alle eingesetzten Server stehen in der Europäischen Union; eine Übermittlung in Drittländer findet nicht statt. Sollte sie künftig erforderlich werden, geschieht dies nur auf Grundlage eines Angemessenheitsbeschlusses oder der Standardvertragsklauseln der EU-Kommission.",
          )
        : p(
            "Beide Dienstleister haben ihren Sitz in Deutschland und verarbeiten die Daten in unserem Auftrag. In welchem Rechenzentrum unser Mailserver betrieben wird, ist an dieser Stelle noch nicht bestätigt; findet dabei eine Verarbeitung außerhalb der Europäischen Union statt, geschieht sie ausschließlich auf Grundlage eines Angemessenheitsbeschlusses oder der Standardvertragsklauseln der EU-Kommission.",
          ),
    ),

    section(
      "7. Deine Rechte",
      p(
        "Du hast das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch gegen Verarbeitungen auf Grundlage berechtigter Interessen (Art. 21 DSGVO). Eine erteilte Einwilligung kannst du jederzeit mit Wirkung für die Zukunft widerrufen.",
      ),
      p(
        "Einiges kannst du unmittelbar selbst tun: dein Profil ändern, ein Gerät freigeben, eine Anlage archivieren und dein Konto löschen (Abschnitt 7a). Eine Ausgabe deiner Messwerte als Datei bieten wir in der Anwendung noch nicht an; sie erledigen wir auf deine Anfrage hin.",
      ),
      p(`Du kannst dich außerdem bei einer Aufsichtsbehörde beschweren. Für uns zuständig ist die ${SUPERVISORY_AUTHORITY.name}, ${SUPERVISORY_AUTHORITY.address}.`),
    ),

    /*
     * **Die Adresse dieses Abschnitts steht in Google Play und im App Store** (HM-163): Die
     * Löschung muss auch ohne die App erreichbar beschrieben sein. Die Schritte
     * nennen deshalb beide Wege, und die Folgen stehen so, wie der Dienst sie
     * ausführt (`AccountDeletionService`, ADR 0044) — einschließlich der
     * Sicherungen, die man sonst verschwiege.
     */
    section(
      "7a. Konto löschen",
      p(
        `Du kannst dein Konto jederzeit selbst löschen — in der WebApp unter „Profil" → „Konto löschen", in der App unter „Mehr" → „Konto löschen". Zur Bestätigung gibst du dein Passwort ein. Kommst du nicht mehr in dein Konto, setz zuerst dein Passwort zurück`,
        ...only<LegalInline>(privacyContact !== null, " oder schreib uns an ", link(`mailto:${privacyContact}`, privacyContact ?? "")),
        ".",
      ),
      p(
        `Gelöscht wird sofort und endgültig: deine Kontodaten und dein Profilbild, deine Sitzungen, und — sofern dir ein eigener Account gehört — alle Anlagen darin samt Geräten, Zählern, Messkonzepten, Zählerständen und Rohmesswerten. Deine Geräte verlieren ihre Zugangsdaten zu unserer Cloud; im Heimnetz arbeiten sie weiter. Wo du nur Bewohner bist oder eine Anlage geteilt bekommen hast, endet dein Zugang; die Daten dort gehören dem Besitzer und bleiben. Hast du Bewohner eingeladen oder eine Anlage geteilt, verlieren diese Personen den Zugang; die Rückfrage vor dem Löschen nennt sie dir.`,
      ),
      p(
        `Bestehen bleibt ein Eintrag, dass ein Konto gelöscht wurde — mit seiner internen Kennung und dem Umfang, ohne Name und Adresse. In unseren nächtlichen Sicherungen sind die Daten noch bis zu ${BACKUP_RETENTION_DAYS} Tage enthalten, bis diese überschrieben werden; wiederhergestellt werden Sicherungen nur nach einem Ausfall, nicht für einzelne Konten.`,
      ),
    ),

    section(
      "8. Speicherdauer im Überblick",
      {
        kind: "table",
        head: ["Daten", "Dauer"],
        rows: [
          [["Kontodaten (Name, Adresse, Passwort-Hash)"], ["bis zur Löschung des Kontos"]],
          [["Nächtliche Sicherung aller Daten"], [`${BACKUP_RETENTION_DAYS} Tage`]],
          [["Eintrag, dass ein Konto gelöscht wurde (interne Kennung, Umfang — ohne Name und Adresse)"], ["unbefristet"]],
          [["Sicherheitscode zur Bestätigung"], [`${CODE_LIFETIME_MINUTES} Minuten`]],
          [["Vorgemerkte, nicht bestätigte Registrierung"], [`${PENDING_REGISTRATION_MAX_AGE_HOURS} Stunden`]],
          [["IP-Adresse zur Begrenzung der Anmeldeversuche"], ["Minuten, nur im Arbeitsspeicher"]],
          [
            ["Sitzung (Anmeldung, letzte Nutzung, Art des Geräts)"],
            [
              `${SESSION_HOURS} Stunden, mit „Angemeldet bleiben" ${SESSION_REMEMBER_DAYS} Tage nach der letzten Nutzung; der Eintrag wird ${EXPIRED_RECORD_GRACE_DAYS} Tage nach dem Ablauf gelöscht`,
            ],
          ],
          [["Änderungsprotokoll zu Anlagen, Geräten, Zählern"], ["bis zur Löschung des Kontos"]],
          [["Rohmesswerte eines Datenpunkts"], [`${RAW_RETENTION_DAYS} Tage`]],
          [["Offene Einladung eines Bewohners oder zu einer geteilten Anlage"], [`${INVITATION_LIFETIME_DAYS} Tage; der Eintrag wird ${EXPIRED_RECORD_GRACE_DAYS} Tage nach dem Ablauf gelöscht`]],
          [
            ["Link zum Zurücksetzen des Passworts oder Bestätigen der Adresse, Kopplungsanfrage eines Geräts"],
            [`bis zum Ablauf; der Eintrag wird ${EXPIRED_RECORD_GRACE_DAYS} Tage nach dem Ablauf gelöscht`],
          ],
          [["Bewohnung einer Wohneinheit (Ein- und Auszug)"], ["bis zur Löschung des Kontos"]],
          [["Freigabe einer Anlage (wer, welche Berechtigung)"], ["bis die Freigabe beendet wird"]],
          [
            ["Aufträge an ein Gerät, etwa eine geänderte Betriebsart der Wallbox"],
            [`${DEVICE_COMMAND_RETENTION_DAYS} Tage ab dem Auftrag; der Verweis auf das auslösende Konto entfällt schon vorher, wenn dieses gelöscht wird`],
          ],
          [["Nutzungsstatistik: einzelne Ereignisse mit Tageskennung"], [`${USAGE_EVENT_RETENTION_DAYS} Tage; der Zufallswert der Tageskennung wird nach Ablauf des Tages gelöscht`]],
          [["Nutzungsstatistik: Tageszahlen ohne Kennung"], ["unbefristet"]],
          [["Feedback samt mitgesendeter Angaben"], ["bis zur Löschung des Kontos"]],
          [["Zählerstände und daraus gebildete Verbräuche"], ["bis zur Löschung des Kontos"]],
          [["Leistungsverlauf als Fünf-Minuten-Mittel"], ["bis zur Löschung des Kontos"]],
          [["Zuletzt gemeldete Diagnose eines Geräts"], ["bis zur nächsten Meldung"]],
          [["Rohmesswerte eines freigegebenen Geräts"], [`wie alle Rohmesswerte: ${RAW_RETENTION_DAYS} Tage nach der Messung`]],
        ],
      },
      p(
        "Rohmesswerte sind die Einzelwerte, die ein Gerät im Minutentakt meldet. Aus ihnen entstehen die Zählerstände und Verbräuche, die deine Auswertungen tragen, und der Leistungsverlauf, zusammengefasst zu Mitteln über je fünf Minuten; beides bleibt erhalten, auch wenn die Einzelwerte dahinter längst gelöscht sind.",
      ),
    ),
  ];
}

/* --------------------------------------------------------------------------
 * Nutzungsbedingungen
 * ----------------------------------------------------------------------- */

/**
 * **Die einzige der drei Seiten, die der Entwurf nicht enthält** — und die
 * dringlichste: Seit HM-002 hält die Registrierung eine Zustimmung fest, und
 * wozu, stand nirgends. Zwei Abschnitte sind keine Formalien, sondern die
 * ehrliche Beschreibung dessen, was das Produkt kann: **§ 5 (keine
 * eichrechtliche Messung)** und **§ 7 (keine zugesicherte Verfügbarkeit)**.
 *
 * Jede Änderung hier ist eine neue `TERMS_VERSION`.
 */
function terms(): readonly LegalBlock[] {
  return [
    {
      kind: "note",
      text: [
        strong("Kurz gefasst:"),
        " HausMeter zeigt Ihnen, was Ihre Zähler messen. Das Gerät gehört Ihnen und misst auch ohne uns weiter. Die angezeigten Werte sind nicht eichrechtlich und ersetzen keine Abrechnung Ihres Versorgers. Sie können jederzeit aufhören; Ihre Daten gehen dann mit.",
      ],
    },

    section(
      "1. Geltungsbereich und Vertragspartner",
      p(
        `Diese Bedingungen gelten für die Nutzung von HausMeter — der WebApp, den Apps und der Cloud-Anbindung der Geräte. Anbieter ist ${PROVIDER.name}, ${PROVIDER.street}, ${PROVIDER.city}. Der Vertrag kommt zustande, wenn Sie Ihre Registrierung mit dem Sicherheitscode aus Ihrer E-Mail bestätigen.`,
      ),
      p(
        `Ein Konto steht unbeschränkt geschäftsfähigen Personen offen. ${PRODUCT.name} ist die Software; `,
        strong("Geräte verkaufen wir nicht hier"),
        `, sondern unter der Marke ${PRODUCT.shopBrand}. Für Kauf, Lieferung und Gewährleistung eines Geräts gelten die Bedingungen des dortigen Kaufvertrags, nicht diese.`,
      ),
    ),

    section(
      "2. Was HausMeter leistet",
      p(
        "HausMeter erfasst Messwerte von Zählern, speichert sie und stellt sie dar. Der Kern ist das Gerät in Ihrem Heimnetz: Es misst und speichert eigenständig, auch ohne Verbindung zu uns.",
      ),
      {
        kind: "list",
        items: [
          [strong("Ohne Konto"), " arbeitet ein Gerät rein lokal. Es sendet uns keine Daten, und wir wissen nicht, dass es existiert."],
          [strong("Mit Konto"), " kommen Messwerte und Gerätezustand zu uns. Daraus entstehen die Historie und die Auswertungen, die ein Gerät allein nicht führen kann."],
        ],
      },
      p(
        "Für Konto und Cloud-Anbindung erheben wir derzeit kein Entgelt. Wenn wir künftig entgeltliche Leistungen anbieten, gilt dafür eine eigene Vereinbarung; an einer bestehenden Nutzung ändert sich dadurch nichts ohne Ihre Zustimmung.",
      ),
    ),

    section(
      "3. Konto und Umfang der Nutzung",
      p(
        "Ein Konto ist für eine Person bestimmt. Halten Sie Ihr Passwort geheim und geben Sie es nicht weiter; wer es kennt, sieht Ihre Verbrauchsdaten. Vermuten Sie, dass jemand anders Zugang hat, ändern Sie es — damit enden alle laufenden Sitzungen, auch auf anderen Geräten.",
      ),
      p("Wie viele Anlagen ein Konto führen darf, richtet sich nach seiner Art. Ein Standardkonto führt eine aktive Anlage; brauchen Sie mehr, sprechen Sie uns an."),
      p(
        "Wer sich über die Einladung zu einer Wohneinheit registriert, erhält ein ",
        strong("Bewohnerkonto"),
        ". Es zeigt die Werte der Wohneinheit für die Zeit, in der Sie dort wohnen, und führt keine eigene Anlage. Legen Sie in Ihrem Profil eine eigene Anlage an, wird es zum Standardkonto; Ihre Wohneinheiten sehen Sie weiterhin. Zurück zum Bewohnerkonto können Sie danach nicht selbst wechseln.",
      ),
    ),

    section("4. Ihre Pflichten", {
      kind: "list",
      items: [
        ["Machen Sie bei der Registrierung zutreffende Angaben und halten Sie Ihre E-Mail-Adresse aktuell — über sie läuft der Weg zurück in Ihr Konto."],
        [
          "Verbinden Sie nur Zähler mit HausMeter, deren Werte Sie erheben dürfen. Bei einem fremden Zähler — etwa dem eines Mieters oder Nachbarn — brauchen Sie dessen Einverständnis.",
        ],
        ["Beachten Sie beim Einbau eines Lesekopfs die Vorgaben Ihres Messstellenbetreibers. Eingriffe in den Zähler selbst sind nicht erlaubt und auch nicht nötig."],
        ["Versuchen Sie nicht, Geräte oder Konten anderer zu beanspruchen, unsere Schnittstellen zu überlasten oder Sicherheitsvorkehrungen zu umgehen."],
      ],
    }),

    section(
      "5. Keine eichrechtliche Messung",
      p(
        "Dies ist der wichtigste Vorbehalt dieser Bedingungen. HausMeter ",
        em("liest"),
        " Ihren Zähler — es ",
        em("ist"),
        " keiner. Die angezeigten Werte sind nicht eichrechtlich und taugen nicht als Grundlage einer Abrechnung gegenüber Dritten. Maßgeblich bleibt allein der Zähler Ihres Messstellenbetreibers und die Abrechnung Ihres Versorgers.",
      ),
      p(
        "Abweichungen sind möglich und keine Fehlfunktion: Ein Lesekopf kann verrutschen, ein Telegramm ausbleiben, eine Verbindung abreißen. Fehlende Werte führen wir als Lücke und rechnen sie nicht hoch — was wir nicht gemessen haben, erfinden wir nicht.",
      ),
    ),

    section(
      "6. Ihre Daten und Ihr Gerät",
      p(
        "Die Messwerte gehören Ihnen. Wir verarbeiten sie, um Ihnen die Auswertungen zu zeigen, und zu keinem anderen Zweck; wir geben sie nicht weiter und verkaufen sie nicht. Wie wir sie im Einzelnen verarbeiten, steht in der ",
        doc("privacy", "Datenschutzerklärung"),
        ".",
      ),
      p("Das Gerät bleibt in Ihrer Hand. Sie können es jederzeit aus Ihrem Konto freigeben oder vor Ort zurücksetzen; es misst danach weiter, nur eben ohne uns."),
    ),

    section(
      "7. Verfügbarkeit",
      p(
        "Wir betreiben den Dienst mit der gebotenen Sorgfalt, sagen aber keine bestimmte Verfügbarkeit zu. Wartungen, Störungen und Ausfälle sind möglich. Nimmt unsere Cloud vorübergehend nichts an, puffert Ihr Gerät die Werte und liefert sie nach, soweit sein Speicher reicht — bei längeren Ausfällen können Werte verloren gehen.",
      ),
      p("Wir dürfen den Dienst weiterentwickeln und einzelne Funktionen ändern oder einstellen. Über wesentliche Einschränkungen informieren wir rechtzeitig."),
    ),

    section(
      "8. Beendigung",
      p(
        "Sie können die Nutzung jederzeit beenden und die Löschung Ihres Kontos verlangen. Mit dem Konto werden Ihre Anlagen, Zähler und Messwerte gelöscht; ein Widerruf ist danach nicht mehr möglich.",
      ),
      p(
        "Wir können ein Konto mit angemessener Frist kündigen und bei erheblichen Verstößen gegen diese Bedingungen sperren. In beiden Fällen erhalten Sie Gelegenheit, Ihre Daten zu sichern.",
      ),
      p(
        "Ein Widerrufsrecht für Verbraucher besteht bei einem unentgeltlichen Dienst nicht; es entsteht erst mit einer entgeltlichen Leistung und wird Ihnen dann gesondert mitgeteilt.",
      ),
    ),

    section(
      "9. Haftung",
      p(
        "Wir haften unbeschränkt bei Vorsatz und grober Fahrlässigkeit sowie für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit. Bei einfacher Fahrlässigkeit haften wir nur für die Verletzung wesentlicher Vertragspflichten — solcher, deren Erfüllung die ordnungsgemäße Durchführung überhaupt erst ermöglicht und auf die Sie vertrauen dürfen — und der Höhe nach begrenzt auf den vorhersehbaren, vertragstypischen Schaden. Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.",
      ),
      p("Für Nachteile, die daraus entstehen, dass jemand die angezeigten Werte anstelle einer eichrechtlichen Messung verwendet, haften wir nicht (§ 5)."),
    ),

    section(
      "10. Änderungen dieser Bedingungen",
      p(
        "Wir können diese Bedingungen ändern, wenn es sachlich gerechtfertigt ist — etwa bei neuen Funktionen oder geänderter Rechtslage. Wir teilen Ihnen die neue Fassung mindestens sechs Wochen vor ihrem Wirksamwerden in Textform mit. Widersprechen Sie nicht bis zum genannten Zeitpunkt, gilt die neue Fassung als angenommen; auf diese Wirkung weisen wir in der Mitteilung gesondert hin. Widersprechen Sie, können beide Seiten den Vertrag zum Wirksamwerden kündigen.",
      ),
      p(`Jede Fassung trägt eine Nummer. Bei Ihrer Registrierung halten wir fest, welcher Sie zugestimmt haben — dies ist Fassung ${TERMS_VERSION}.`),
    ),

    section(
      "11. Schlussbestimmungen",
      p(
        "Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Sind Sie Verbraucher, bleiben die zwingenden Verbraucherschutzvorschriften Ihres Aufenthaltsstaates unberührt. Ist eine Bestimmung unwirksam, bleiben die übrigen wirksam.",
      ),
      p("Die Angaben zum Anbieter finden Sie im ", doc("imprint", "Impressum"), "."),
    ),
  ];
}

/* --------------------------------------------------------------------------
 * Die drei Dokumente
 * ----------------------------------------------------------------------- */

export const APP_LEGAL_DOCUMENTS: Record<LegalDocumentId, LegalDocumentContent> = {
  imprint: {
    id: "imprint",
    title: "Impressum",
    lead: `${PRODUCT.name} ist ein Angebot der ${PROVIDER.name} · Angaben gemäß § 5 DDG`,
    blocks: imprint(),
  },
  privacy: {
    id: "privacy",
    title: "Datenschutzerklärung",
    lead: "Informationen nach Art. 13 und 14 DSGVO",
    blocks: privacy(),
  },
  terms: {
    id: "terms",
    title: "Nutzungsbedingungen",
    lead: `Fassung ${TERMS_VERSION} · für die Nutzung von ${PRODUCT.name}`,
    blocks: terms(),
  },
};
